/**
 * Datentypengruppen für die IDS-Property-Facette (#2312).
 *
 * <h2>Warum es das gibt</h2>
 * IFC kennt sehr viele Datentypen — allein in IFC4 gibt es 92 Maße, die auf `real` hinauslaufen
 * (`IfcLengthMeasure`, `IfcAreaMeasure`, `IfcCountMeasure`, …). Wer „irgendeine Zahl" fordern will,
 * müsste sich für genau einen davon entscheiden und schließt damit alle anderen aus.
 *
 * <h2>Warum das anders funktioniert als bei den Entitätengruppen</h2>
 * `ids:property/@dataType` ist im IDS-Schema ein **einzelner** Wert (`ids:upperCaseName`, Pattern
 * `[A-Z]+`) und keine Restriktion — eine Aufzählung ist dort also nicht möglich. Anders als
 * Entitätengruppen (#2310) lässt sich eine Datentypengruppe deshalb nicht in Standard-IDS
 * ausdrücken.
 *
 * Gespeichert wird stattdessen ein BIC-Token als `dataType` (`TEXT`, `ZAHL`, `GANZZAHL`,
 * `WAHRHEITSWERT`). Es erfüllt das XSD-Pattern und passiert die Validierung; `ids_checker.py`
 * löst es beim Prüfen zu einer ODER-Bedingung auf. Der Preis ist bewusst gewählt: ein so
 * exportiertes `.ids` ist für Fremdwerkzeuge kein gültiger IFC-Typname mehr.
 *
 * <h2>Warum Grundtypen und keine Namensliste</h2>
 * Die Zugehörigkeit ergibt sich aus dem EXPRESS-Grundtyp, den der Generator aus dem IFC-Schema
 * mitliefert. Eine handgepflegte Liste von Typnamen wäre bei 92 Maßen unvollständig und müsste bei
 * jeder IFC-Version nachgezogen werden.
 */

import {IFC_VALUE_PRIMITIVES, IfcSchemaVersion, IfcValuePrimitive} from './ifc-schema.generated';

/** Eine wählbare Sammlung von IFC-Datentypen. */
export interface IfcDataTypeGroup {
  /** Token, das als `dataType` im XML landet. Nur Großbuchstaben (XSD-Pattern `[A-Z]+`). */
  token: string;
  /** Anzeigename im Editor und im Bericht. */
  label: string;
  /** EXPRESS-Grundtypen, die zur Gruppe gehören. */
  primitives: IfcValuePrimitive[];
  /** Kurzerklärung für den Tooltip. */
  hint: string;
}

/**
 * Die vier Gruppen aus dem Ticket.
 *
 * „Ganzzahl" ist absichtlich eine Teilmenge von „Zahl": wer nur ganze Zahlen zulassen will, wählt
 * die engere Gruppe; „Zahl" lässt jede numerische Angabe zu.
 */
export const IFC_DATA_TYPE_GROUPS: IfcDataTypeGroup[] = [
  {
    token: 'TEXT',
    label: 'Text',
    primitives: ['string'],
    hint: 'Jeder textbasierte IFC-Datentyp',
  },
  {
    token: 'ZAHL',
    label: 'Zahl',
    primitives: ['real', 'number', 'integer'],
    hint: 'Jeder numerische IFC-Datentyp, inklusive Maße und ganzer Zahlen',
  },
  {
    token: 'GANZZAHL',
    label: 'Ganzzahl',
    primitives: ['integer'],
    hint: 'Nur ganzzahlige IFC-Datentypen',
  },
  {
    token: 'WAHRHEITSWERT',
    label: 'Wahrheitswert',
    primitives: ['boolean', 'logical'],
    hint: 'IfcBoolean und IfcLogical',
  },
];

/** Fallback-Version, wenn eine Spezifikation keine IFC-Version gesetzt hat. */
const FALLBACK_VERSIONS: IfcSchemaVersion[] = ['IFC4'];

/** Gruppe zu einem `dataType`-Wert, oder `null`, wenn es ein echter IFC-Typ ist. */
export function dataTypeGroupOf(dataType: string | null | undefined): IfcDataTypeGroup | null {
  const token = (dataType ?? '').trim().toUpperCase();
  if (!token) return null;
  return IFC_DATA_TYPE_GROUPS.find(group => group.token === token) ?? null;
}

/** True, wenn der Wert eine Gruppe und kein IFC-Datentyp ist. */
export function isDataTypeGroup(dataType: string | null | undefined): boolean {
  return dataTypeGroupOf(dataType) !== null;
}

/**
 * Die IFC-Datentypen einer Gruppe, über die angegebenen Versionen vereinigt.
 *
 * Alphabetisch, damit Tooltip und Bericht stabil bleiben.
 */
export function dataTypeGroupMembers(
  group: IfcDataTypeGroup, versions: readonly string[] = [],
): string[] {
  const wanted = new Set<IfcValuePrimitive>(group.primitives);
  const members = new Set<string>();
  for (const version of schemaVersions(versions)) {
    const table = IFC_VALUE_PRIMITIVES[version];
    for (const [name, primitive] of Object.entries(table)) {
      if (wanted.has(primitive)) members.add(name);
    }
  }
  return [...members].sort();
}

/** Kopfzeile für den Tooltip einer Gruppe: Kurzerklärung plus Trefferzahl. */
export function dataTypeGroupTooltipTitle(group: IfcDataTypeGroup, versions: readonly string[] = []): string {
  return `${group.hint} (${dataTypeGroupMembers(group, versions).length})`;
}

/** Detailzeile für den Tooltip einer Gruppe: die enthaltenen IFC-Datentypen (gekürzt). */
export function dataTypeGroupMemberList(
  group: IfcDataTypeGroup, versions: readonly string[] = [], limit = 12,
): string {
  const members = dataTypeGroupMembers(group, versions);
  const shown = members.slice(0, limit).join(', ');
  const rest = members.length - limit;
  return rest > 0 ? `${shown} … und ${rest} weitere` : shown;
}

/**
 * Tooltip einer Gruppe als ein String: Erklärung, Trefferzahl und die enthaltenen IFC-Datentypen.
 * Für Fließtext (z.B. den Bericht) - im Editor-Tooltip stattdessen {@link dataTypeGroupTooltipTitle}
 * und {@link dataTypeGroupMemberList} getrennt verwenden, sonst wird auch die lange Typliste fett
 * dargestellt (`bic-tooltip` rendert `tooltip` immer in `font-medium`, nur `tooltipSubtext` normal).
 */
export function dataTypeGroupTooltip(
  group: IfcDataTypeGroup, versions: readonly string[] = [], limit = 12,
): string {
  return `${dataTypeGroupTooltipTitle(group, versions)}: ${dataTypeGroupMemberList(group, versions, limit)}`;
}

/** Alle im Schema bekannten IFC-Datentypen, über die angegebenen Versionen vereinigt. */
export function knownDataTypes(versions: readonly string[] = []): string[] {
  const names = new Set<string>();
  for (const version of schemaVersions(versions)) {
    for (const name of Object.keys(IFC_VALUE_PRIMITIVES[version])) names.add(name);
  }
  return [...names].sort();
}

/** EXPRESS-Grundtyp eines rohen IFC-Datentypnamens (z.B. `IfcLengthMeasure` → `real`), oder `null`. */
export function primitiveOfDataType(
  dataType: string, versions: readonly string[] = [],
): IfcValuePrimitive | null {
  for (const version of schemaVersions(versions)) {
    const primitive = IFC_VALUE_PRIMITIVES[version][dataType];
    if (primitive) return primitive;
  }
  return null;
}

function schemaVersions(versions: readonly string[]): IfcSchemaVersion[] {
  const known = (versions ?? [])
    .map(version => (version ?? '').trim().toUpperCase())
    .filter((version): version is IfcSchemaVersion => version in IFC_VALUE_PRIMITIVES);
  return known.length > 0 ? [...new Set(known)] : FALLBACK_VERSIONS;
}
